import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useResolvedColors } from '@/lib/useResolvedColor';
import { ChartLegend } from './ChartLegend';
import { ChartTooltip } from './ChartTooltip';
import { QuarterTick, profileChartStyles as s } from './ProfileSection';
import styles from './MiniLineChart.module.css';

export interface MiniLineSeries {
  key: string;
  label: string;
  /** Colour token. */
  color: string;
}

export interface MiniLineChartProps {
  /** One row per quarter: { label: "Q2 2026", [series.key]: number }. */
  points: Record<string, string | number | undefined>[];
  series: MiniLineSeries[];
  /** Formats axis ticks and tooltip values. */
  format: (value: number) => string;
  /** Small caps caption above the plot. */
  title?: string;
  domain?: [number | 'auto', number | 'auto'];
  height?: number;
}

/** A compact multi-series line chart over quarters, for a secondary trend inside a section. */
export function MiniLineChart({ points, series, format, title, domain = ['auto', 'auto'], height = 180 }: MiniLineChartProps) {
  const colors = useResolvedColors(series.map((x) => x.color));
  return (
    <div className={styles.chart}>
      {title && <span className={styles.title}>{title}</span>}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={points} margin={{ top: 8, right: 28, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
          <XAxis dataKey="label" axisLine={false} tickLine={false} interval={0} height={44} tick={QuarterTick} />
          <YAxis
            className={s.axis}
            axisLine={false}
            tickLine={false}
            width={44}
            domain={domain}
            tickFormatter={(v) => format(Number(v))}
          />
          <Tooltip
            content={({ active, payload, label }) =>
              active && payload?.length ? (
                <ChartTooltip
                  title={label}
                  rows={payload.map((p) => ({
                    label: series.find((x) => x.key === p.dataKey)?.label ?? String(p.dataKey),
                    value: format(Number(p.value)),
                    color: String(p.color),
                  }))}
                />
              ) : null
            }
          />
          {series.map((x, i) => (
            <Line key={x.key} dataKey={x.key} stroke={colors[i]} strokeWidth={2} dot={false} isAnimationActive={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <ChartLegend
        className={styles.legend}
        items={series.map((x, i) => ({ label: x.label, color: colors[i], shape: 'line' as const }))}
      />
    </div>
  );
}
